import React, { useState } from 'react';
import { useData, usePaginatedResponse } from '../../model/data';
import { Container } from '../../components/Container/Container';
import { Card } from '../../components/Card/Card';
import { Pagination } from '../../components/Pagination/Pagination';
import { serializeQuery } from '../../model/filter';
import { useMeta } from '../../model/meta';
import { Icon } from '../../components/Icon/Icon';
import { ControlledModal } from '../../components/Modal/ControlledModal';
import { getContracts, ContractInfo } from '../../model/contract';
import { useQueryAsState } from '../../model/history';
import { SortableList } from '../../components/SortableList/SortableList';
import { contractPath } from '../../routes';
import { ContractForm } from './modals/ContractForm';
import { formatDateTime } from '../../util/util';
import './Contracts.css';
import { FilterBar } from '../../components/FilterBar/FilterBar';
import { t } from '../../locales/translate';
import { TableBlock } from '../../components/TableBlock/TableBlock';
import { compact } from 'lodash';

export function Contracts(): JSX.Element {
  const [params] = useQueryAsState();
  const paginator = usePaginatedResponse(
    (page, perPage) =>
      getContracts({
        term: serializeQuery(params.query),
        page,
        perPage,
        sortBy: 'created_at',
        sortDir: params.sortDir || 'desc',
        archived: params.archived,
      }),
    JSON.stringify(params)
  );
  useMeta({
    title: t.model.contract._plural,
  });

  const contracts = useData((db) => compact(paginator.currentIds.map((e) => db.table('contracts').find(e))));

  const [showAddModal, setShowAddModal] = useState(false);

  return (
    <Container className="contracts">
      <Card
        title={t.model.contract._plural}
        elevation={2}
        pagination={<Pagination {...paginator} />}
        actions={
          <Icon
            name="plus"
            onClick={() => {
              setShowAddModal(true);
            }}
          />
        }
        fullbleed={!paginator.error}
      >
        <SortableList
          className="contracts-list"
          loading={paginator.loading}
          items={contracts.map((contract) => ({
            ...contract,
            createdAt: formatDateTime(contract.createdAt),
            products: contract.products.length,
            companies: contract.companies.map((company) => company.name),
          }))}
          rowLink={(contract) => contractPath(contract.id)}
          actions={<FilterBar filters={['query', 'archived']} />}
          columns={[
            {
              dataKey: 'reference',
              label: t.model.reference._name,
            },
            {
              dataKey: 'name',
              label: t.general.name,
              cellRenderer: (contract) => (
                <TableBlock header={contract.name} footer={contract.archived && 'Archived'} />
              ),
            },
            {
              dataKey: 'companies',
              label: t.model.company._plural,
              cellRenderer: (row) => row.companies.join(' - '),
            },
            {
              dataKey: 'products',
              label: t.model.product._plural,
            },
            {
              dataKey: 'createdAt',
              label: t.model.datetime.createdAt,
            },
            {
              dataKey: 'status',
              label: t.model.status._name,
            },
          ]}
        />
      </Card>
      <ControlledModal<ContractInfo>
        open={showAddModal}
        model={{ name: '', archived: false }}
        onClose={() => setShowAddModal(false)}
        title={t.actions.create(t.model.contract._name)}
        size="small"
      >
        {(controller) => <ContractForm controller={controller} />}
      </ControlledModal>
    </Container>
  );
}
